import React, { useState } from "react";

const ChevronDown = ({ open }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{
      transition: "transform 0.25s ease",
      transform: open ? "rotate(180deg)" : "rotate(0deg)",
      color: "#555",
    }}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

// ─── Lock Icon ───────────────────────────────────────────────────────────────
const LockIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
  </svg>
);

const ReferralRow = ({ referral, index, isLast }) => {
  const shiftsColor = referral.shiftsCompleted >= 10 ? "#09A22F" : "#E24B4A";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        padding: "10px 14px",
        borderBottom: isLast ? "none" : "1px solid #f0f0f5",
        gap: 8,
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 600, color: "#111", width: 18, flexShrink: 0 }}>
        {index}.
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#111" }}>
          {referral.name}
        </div>
        <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
          {referral.phone}
        </div>
      </div>
      <div style={{ textAlign: "center", flexShrink: 0, minWidth: 70 }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: shiftsColor, lineHeight: 1.1 }}>
          {referral.shiftsCompleted}
        </div>
        <div style={{ fontSize: 10, color: "#777", marginTop: 1 }}>
          Shifts Done
        </div>
      </div>
    </div>
  );
};

const StatusMessage = ({ message, type }) => {
  const colorMap = {
    purple: "#4A00E0",
    green: "#09A22F",
    red: "#B60C0C",
  };
  return (
    <div
      style={{
        padding: "0 16px 12px",
        fontSize: 12,
        fontWeight: 600,
        color: colorMap[type] || "#555",
      }}
    >
      {message}
    </div>
  );
};

const detectStatusType = (message, referralAmount, isCurrentMonth) => {
  if (!message) return null;
  if (isCurrentMonth) return "purple";
  if (referralAmount > 0 && message.toLowerCase().includes("hurray"))
    return "green";
  if (
    message.toLowerCase().includes("not completed") ||
    message.toLowerCase().includes("didn't meet") ||
    message.toLowerCase().includes("will not get") ||
    message.toLowerCase().includes("don't have")
  )
    return "red";
  return "green";
};

// ─── ReferralPayoutCard ──────────────────────────────────────────────────────
const ReferralPayoutCard = ({
  month,
  totalShifts,
  referralAmount,
  totalReferrals,
  referrals = [],
  noPayoutMessage,
  isExpanded,
  onToggle,
  isCurrentMonth = false,
  successMessage,
  eligibleReferralsCount,
}) => {
  // Determine the status message and its type
  const statusMessage = noPayoutMessage || successMessage || null;
  const statusType = detectStatusType(
    statusMessage,
    referralAmount,
    isCurrentMonth
  );

  const cardStyle = {
    background: isCurrentMonth ? "#eef0ff" : "#fff",
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  };

  // Calculate eligible referrals count
  const eligibleReferrals = eligibleReferralsCount;

  // Fixed height for scrollable area
  const ROW_HEIGHT = 66; // Height per row including padding
  const MAX_VISIBLE_ROWS = 4;
  
  const getScrollableHeight = () => {
    const totalRows = referrals.length;
    if (totalRows <= MAX_VISIBLE_ROWS) {
      return totalRows * ROW_HEIGHT;
    }
    return MAX_VISIBLE_ROWS * ROW_HEIGHT;
  };

  const needsScroll = referrals.length > MAX_VISIBLE_ROWS;

  return (
    <div style={cardStyle}>
      {/* Card Header */}
      <div style={{ padding: "12px 16px 0" }}>
        {/* Month title */}
        <div
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: "#111",
            marginBottom: 10,
          }}
        >
          {month}
          {isCurrentMonth && (
            <span style={{ fontWeight: 400, color: "#555", fontSize: 12 }}>
              {" "}
              (Current Month)
            </span>
          )}
        </div>

        {/* Stats row - all three in same row */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            marginBottom: 12,
            gap: 8,
          }}
        >
          {/* My Shifts */}
          <div style={{ textAlign: "center", flex: 1 }}>
            <div
              style={{ fontSize: 18, fontWeight: 800, color: "#111", lineHeight: 1.2, minHeight: 32 }}
            >
              {totalShifts}
            </div>
            <div style={{ fontSize: 10, color: "#777", marginTop: 2 }}>
              My Shifts
            </div>
          </div>

          {/* Referral Amount */}
          <div style={{ textAlign: "center", flex: 1 }}>
            <div style={{ minHeight: 32, display: "flex", alignItems: "center", justifyContent: "center" }}>
              {isCurrentMonth ? (
                <div
                  style={{
                    background: "#d6d9ff",
                    borderRadius: 18,
                    padding: "4px 10px",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    fontSize: 14,
                    fontWeight: 800,
                    color: "#3b3f7c",
                  }}
                >
                  <LockIcon />
                  <span>₹{referralAmount}</span>
                </div>
              ) : (
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 800,
                    color: referralAmount > 0 ? "#22c55e" : "#111",
                    lineHeight: 1.2,
                  }}
                >
                  ₹{referralAmount}
                </div>
              )}
            </div>
            <div style={{ fontSize: 10, color: "#777", marginTop: 2 }}>
              Referral Amount
            </div>
          </div>

          {/* Eligible Referrals */}
          <div style={{ textAlign: "center", flex: 1 }}>
            <div
              style={{ fontSize: 18, fontWeight: 800, color: "#111", lineHeight: 1.2, minHeight: 32 }}
            >
              {eligibleReferrals}
            </div>
            <div style={{ fontSize: 10, color: "#777", marginTop: 2 }}>
              Eligible Referrals
            </div>
          </div>
        </div>
      </div>

      {/* Dropdown Toggle Row */}
      <div style={{ margin: "0 10px 10px", borderRadius: 10, background: "#fff", overflow: "hidden" }}>
        <div
          onClick={onToggle}
          style={{
            padding: "8px 12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            userSelect: "none",
            backgroundColor: "#fafafa",
            borderBottom: isExpanded ? "1px solid #f0f0f5" : "none",
          }}
        >
          <span style={{ fontSize: 13, color: "#222", fontWeight: 500 }}>
            Total Referrals :{" "}
            <span style={{ color: "#3b3fc8", fontWeight: 700 }}>{totalReferrals}</span>
          </span>
          <ChevronDown open={isExpanded} />
        </div>

        <div
          style={{
            overflowY: needsScroll ? "auto" : "hidden",
            overflowX: "hidden",
            maxHeight: isExpanded ? getScrollableHeight() : 0,
            transition: "max-height 0.35s ease-in-out",
          }}
        >
          {referrals.map((referral, idx) => (
            <ReferralRow
              key={idx}
              referral={referral}
              index={idx + 1}
              isLast={idx === referrals.length - 1}
            />
          ))}
        </div>
      </div>

      {/* Status / Info Message */}
      {statusMessage && (
        <StatusMessage message={statusMessage} type={statusType} />
      )}
    </div>
  );
};

export default ReferralPayoutCard;